import type { Tracker } from './tracker'
import type { ClickEventPayload } from './types'

// 连续失败超过此次数后停止心跳
const MAX_FAIL_COUNT = 5

// 点击事件上报器：立即上报 + 防抖保护 + 失败兜底
export class ClickReporter {
  private request?: (payload: ClickEventPayload) => Promise<void>
  private beaconUrl?: string
  debug = false
  // 防抖：同一 key+type 300ms 内只上报最后一次
  private debounceMap = new Map<string, ReturnType<typeof setTimeout>>()
  // 上报失败的事件暂存，页面卸载时用 sendBeacon 兜底
  private failedQueue: ClickEventPayload[] = []

  setRequest(fn: (payload: ClickEventPayload) => Promise<void>) {
    this.request = fn
  }

  // 设置 sendBeacon 兜底地址（与后端协商的批量接收端点）
  setBeaconUrl(url: string) {
    this.beaconUrl = url
  }

  report(payload: ClickEventPayload) {
    const dedupeKey = `${payload.clickEventKey}:${payload.clickEventType}`
    const existing = this.debounceMap.get(dedupeKey)
    if (existing) clearTimeout(existing)

    this.debounceMap.set(
      dedupeKey,
      setTimeout(() => {
        this.debounceMap.delete(dedupeKey)
        this._send(payload)
      }, 300),
    )
  }

  private _send(payload: ClickEventPayload) {
    if (this.debug) {
      console.log('[ClickReporter]', payload.clickEventType, payload.clickEventKey, payload.clickEventParentKey)
    }
    if (!this.request) {
      this.failedQueue.push(payload)
      return
    }
    this.request(payload).catch(() => {
      this.failedQueue.push(payload)
    })
  }

  // 页面卸载时用 sendBeacon 上报失败队列（需后端支持批量接收）
  flushWithBeacon() {
    if (!this.beaconUrl || this.failedQueue.length === 0) return
    for (const item of this.failedQueue) {
      navigator.sendBeacon(this.beaconUrl, JSON.stringify(item))
    }
    this.failedQueue = []
  }

  // 注册页面卸载监听（在应用初始化时调用一次）
  setupUnloadHandler() {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flushWithBeacon()
    })
    window.addEventListener('beforeunload', () => this.flushWithBeacon())
  }
}

// 批量上报调度器：定时上报 + 页面关闭前强制上报
export class Reporter {
  private heartbeatRequest?: () => Promise<{ code: string }>
  private heartbeatTimer?: number
  private failCount = 0

  schedule(tracker: Tracker) {
    // 每 10s 批量上报一次
    setInterval(() => tracker.flush(), 10000)

    // 页面切到后台或关闭前强制上报
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') tracker.flush()
    })
    window.addEventListener('beforeunload', () => tracker.flush(true))
  }

  // 设置心跳上报函数（由外部注入）
  setHeartbeatRequest(fn: () => Promise<{ code: string }>) {
    this.heartbeatRequest = fn
  }

  // 启动心跳上报：登录后每10秒上报一次
  startHeartbeat() {
    if (!this.heartbeatRequest) {
      console.warn('Heartbeat request function not set')
      return
    }

    this.stopHeartbeat() // 先停止之前的定时器
    this.failCount = 0   // 重置失败计数

    const sendHeartbeat = async () => {
      try {
        const result = await this.heartbeatRequest?.()
        // code: '10004' 表示 token 失效，停止上报
        if (result?.code === '10004') {
          this.stopHeartbeat()
          return
        }
        this.failCount = 0 // 成功则重置失败计数
      } catch (error: any) {
        // 全局错误处理器抛出 BizError 时，从 error.info.errorCode 中读取业务码
        if (error?.info?.errorCode === '10004') {
          this.stopHeartbeat()
          return
        }
        this.failCount++
        if (this.failCount >= MAX_FAIL_COUNT) {
          console.warn(`[Heartbeat] 连续失败 ${MAX_FAIL_COUNT} 次，停止上报`)
          this.stopHeartbeat()
        }
      }
    }

    // 立即发送一次
    sendHeartbeat()

    // 每10秒上报一次
    this.heartbeatTimer = window.setInterval(sendHeartbeat, 10000)
  }

  // 停止心跳上报
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = undefined
    }
  }
}
